import React from 'react';
import { Mui } from '@osu/react-ui';
import { useLocation } from 'react-router-dom';
import { AUTH_ROLES, PROGRAMS } from '../../util/constants';
import { deriveProgramTypeFromPathname } from '../../util/scholars/scholarsScope';
import { useScholarsOptInNotification } from '../../util/scholars/scholarsOptInNotification';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';

// has587 / has6xx — Scholars Opt-In "new applicant" notification.
//
// The notification count is now rendered as an iOS-style corner badge ON the "Opt-In List" nav link
// itself (see OptInNavBadge below) rather than as a standalone bell + toggle in the app bar. The on/off
// toggle moved onto the Opt-In List report page (ScholarsAlternateList) where it is labeled explicitly.
//
// Visibility: gated to EXACTLY the two roles that can see the Opt-In List report itself — SCHOLARS-ADMIN
// and SCHOLARS-PROGRAM-MANAGER — and only while in Scholars scope (`/scholars/*`). We check raw `roles`
// (not useRoleAuthentication's path-aware isScholars* flags, which are only true on routes that grant the
// role — the navbar renders on every route).
const ALLOWED_ROLES = [AUTH_ROLES.SCHOLARS_ADMIN, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER];

/**
 * Hook: resolve the badge count to show on the Opt-In List nav link.
 *
 * Returns 0 (no badge) when the user is out of Scholars scope, lacks the role, or the notification is
 * toggled off — matching the previous bell behavior. The underlying query is skipped unless the controls
 * are actually visible, so no request fires for ineligible users.
 *
 * @returns {{ count: number, visible: boolean }}
 */
export function useOptInNavBadge() {
    const { roles = [] } = useRoleAuthentication();
    const { pathname } = useLocation();

    const isScholarsScope = deriveProgramTypeFromPathname(pathname) === PROGRAMS.SCHOLARS.KEY;
    const hasRole = ALLOWED_ROLES.some((r) => roles.includes(r));
    const shouldShow = isScholarsScope && hasRole;

    const { newCount, enabled } = useScholarsOptInNotification({ active: shouldShow });

    // When disabled (toggle off on the report page) suppress the count so no badge renders.
    const count = shouldShow && enabled ? newCount : 0;
    return { count, visible: shouldShow };
}

/**
 * Wrap the Opt-In List nav link (inline button or collapsed menu item) with an iOS-style corner badge
 * showing the number of new applicants. Renders children unchanged when there is nothing to badge.
 *
 * @param {object} props
 * @param {number} props.count - new-applicant count (0 => no badge)
 * @param {React.ReactNode} props.children
 */
export function OptInNavBadge({ count = 0, children }) {
    const hasNew = count > 0;
    const label = hasNew
        ? `${count} new applicant${count === 1 ? '' : 's'} added to opt-in report`
        : undefined;

    return (
        <Mui.Badge
            badgeContent={count}
            color="error"
            overlap="rectangular"
            // Anchor the badge just past the end of the "Opt-In List" label (not out in the gap before
            // the next link). The nav Button carries a trailing 1rem margin, so the badge's box extends
            // ~16px to the right of the visible text; a positive `right` offset pulls the badge back
            // leftward so it nearly touches the label, iOS-style.
            sx={{ '& .MuiBadge-badge': { top: 4, right: 14 } }}
            aria-label={label}
            data-testid="scholars-opt-in-nav-badge"
        >
            {children}
        </Mui.Badge>
    );
}

// Back-compat default export is intentionally a no-op: the standalone app-bar control was removed in
// favor of the nav-link badge (useOptInNavBadge / OptInNavBadge). Kept so any stray import does not break
// the build; renders nothing.
export default function ScholarsOptInNotification() {
    return <React.Fragment />;
}
